// src/services/adminDevelopments.service.ts
import { http } from '../api/http';

export type DevelopmentStatus = 'PLANNING' | 'PRE_SALE' | 'UNDER_CONSTRUCTION' | 'COMPLETED' | 'CANCELLED';
export type ProjectState = 'DRAFT' | 'PUBLISHED' | 'IN_EXECUTION' | 'FINISHED' | 'CANCELLED';

export type ApiResponse<T> =
  | { ok: true; data: T; pagination?: Pagination }
  | { ok: false; message: string; code?: string };

export type Pagination = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export type AdminDevelopmentListItem = {
  id: number;
  name: string;
  slug: string;
  city: string;
  state: string;
  country: string;
  status: DevelopmentStatus;
  project_state?: ProjectState;
  is_active: boolean;
  is_published: boolean;
  documentation_status?: 'NONE' | 'UPLOADED' | 'IN_REVIEW' | 'OBSERVED' | 'APPROVED';
  sort_order: number;
  cover_image_url?: string | null;
  created_at: string;
  updated_at: string;
};

export type AdminDevelopmentImage = {
  id: number;
  image_url: string;
  alt?: string | null;
  caption?: string | null;
  sort_order: number;
  is_cover: boolean;
};

export type AdminUnitSummary = {
  id: number;
  name?: string | null;
  price_mxn: number;
  available: number;
  reserved: number;
  sold: number;
  is_active: boolean;
};

export type AdminDevelopmentDetail = {
  id: number;
  name: string;
  slug: string;
  summary?: string | null;
  description?: string | null;
  country: string;
  state: string;
  city: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  status: DevelopmentStatus;
  project_state?: ProjectState;
  progress_pct?: number | null;
  seo_title?: string | null;
  seo_description?: string | null;
  og_image_url?: string | null;
  video_url?: string | null;
  investment_start_at?: string | null;
  investment_end_at?: string | null;
  min_blocks_per_order?: number | null;
  max_blocks_per_user?: number | null;
  funding_goal?: number | null;
  term_months?: number | null;
  is_active: boolean;
  is_published: boolean;
  publish_from?: string | null;
  publish_to?: string | null;
  sort_order: number;
  risk_level_id?: number | null;
  images: AdminDevelopmentImage[];
  documents?: any[];
  units?: AdminUnitSummary[];
  created_at: string;
  updated_at: string;
};

export type FinalizeDevelopmentResult = {
  developmentId: number;
  status: string;
  totalOrdersFound: number;
  closedOrders: number;
  alreadyClosedOrders: number;
  skippedOrders: number;
  failedOrders: number;
  message?: string;
};

export type CreateDevelopmentDto = {
  developer_id?: number | null;
  name: string;
  slug?: string;
  summary?: string | null;
  description?: string | null;
  country: string;
  state: string;
  city: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  status: DevelopmentStatus;
  project_state?: ProjectState;
  progress_pct?: number | null;
  seo_title?: string | null;
  seo_description?: string | null;
  og_image_url?: string | null;
  video_url?: string | null;
  investment_start_at?: string | null;
  investment_end_at?: string | null;
  min_blocks_per_order?: number | null;
  max_blocks_per_user?: number | null;
  funding_goal?: number | null;
  term_months?: number | null;
  sort_order?: number;
  risk_level_id?: number | null;
  publish_from?: string | null;
  publish_to?: string | null;
  is_active?: boolean;
};

export type UpdateDevelopmentDto = Partial<CreateDevelopmentDto>;

export type AdminDevelopmentListQuery = {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: DevelopmentStatus | '';
  project_state?: ProjectState | '';
  isActive?: boolean | '';
  isPublished?: boolean | '';
};

const toQueryString = (query: AdminDevelopmentListQuery) => {
  const p = new URLSearchParams();
  if (query.page != null) p.set('page', String(query.page));
  if (query.pageSize != null) p.set('pageSize', String(query.pageSize));
  if (query.q) p.set('q', query.q);
  if (query.status) p.set('status', query.status);
  if (query.project_state) p.set('project_state', query.project_state);
  if (query.isActive !== '') p.set('isActive', String(query.isActive));
  if (query.isPublished !== '') p.set('isPublished', String(query.isPublished));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const AdminDevelopmentsService = {
  async list(query: AdminDevelopmentListQuery) {
    return http<ApiResponse<{ items: AdminDevelopmentListItem[] }>>(`/admin/developments${toQueryString(query)}`);
  },

  async getById(id: number | string) {
    return http<ApiResponse<AdminDevelopmentDetail>>(`/admin/developments/${id}`);
  },

  async create(payload: CreateDevelopmentDto) {
    return http<ApiResponse<{ id: number }>>('/admin/developments', { method: 'POST', body: payload });
  },

  async update(id: number | string, payload: UpdateDevelopmentDto) {
    return http<ApiResponse<AdminDevelopmentDetail>>(`/admin/developments/${id}`, { method: 'PATCH', body: payload });
  },

  async setActive(id: number | string, is_active: boolean) {
    return http<ApiResponse<{ id: number; is_active: boolean }>>(`/admin/developments/${id}/active`, {
      method: 'PATCH',
      body: { is_active }
    });
  },

  async setPublished(id: number | string, payload: { is_published: boolean; publish_from?: string | null; publish_to?: string | null }) {
    return http<ApiResponse<{ id: number; is_published: boolean }>>(`/admin/developments/${id}/publish`, {
      method: 'PATCH',
      body: payload
    });
  },

  async finalize(id: number | string) {
    return http<FinalizeDevelopmentResult>(`/admin/developments/${id}/finalize`, { method: 'PATCH' });
  }
};
